import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageInboxItem, EmailClassificationCategory, EmailClassificationClient } from '../../../web-api-client';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-change-classification-modal',
  templateUrl: './change-classification-modal.component.html',
  styleUrls: ['./change-classification-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeClassificationModalComponent implements OnInit {
  @Input() message: MessageInboxItem;

  public onClose: Subject<boolean> = new Subject();
  public selectedClassification: EmailClassificationCategory | null = null;
  public isSaving: boolean = false;
  public EmailClassificationCategory = EmailClassificationCategory;

  // Available classification options (excluding Unknown)
  public classificationOptions = [
    { value: EmailClassificationCategory.Zayavlenie, label: 'Заявление' },
    { value: EmailClassificationCategory.Other, label: 'Други' }
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private classificationClient: EmailClassificationClient,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize with current classification if exists
    // Get the latest classification from the array
    const latestClassification = this.getLatestClassification();
    if (latestClassification) {
      this.selectedClassification = latestClassification.category;
    }
  }

  /**
   * Get the latest classification for the message
   */
  public getLatestClassification(): any | undefined {
    if (!this.message.emailClassifications || this.message.emailClassifications.length === 0) {
      return undefined;
    }

    // Return the most recent classification (sorted by classifiedAt descending)
    return this.message.emailClassifications
      .sort((a, b) => {
        const dateA = a.classifiedAt ? new Date(a.classifiedAt).getTime() : 0;
        const dateB = b.classifiedAt ? new Date(b.classifiedAt).getTime() : 0;
        return dateB - dateA;
      })[0];
  }

  /**
   * Handle save button click - show confirmation dialog first
   */
  onSave(): void {
    if (this.selectedClassification === null) {
      Swal.fire({
        title: 'Внимание',
        text: 'Моля, изберете класификация',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Show confirmation dialog
    Swal.fire({
      title: 'Потвърждение',
      text: 'Сигурни ли сте, че искате да промените класификацията?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Да',
      cancelButtonText: 'Отказ',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.performSave();
      }
    });
  }

  /**
   * Perform the actual save operation
   */
  private performSave(): void {
    this.isSaving = true;
    this.changeDetectorRef.detectChanges();

    this.classificationClient.reclassifyEmail(this.message.messageId, this.selectedClassification!)
      .subscribe({
        next: (response) => {
          // Create new classification object for the reclassification
          const newClassification: any = {
            messageInboxItemId: this.message.id,
            category: this.selectedClassification!,
            isManualClassification: true,
            classifiedAt: new Date()
          };

          // Add to emailClassifications array (or create array if it doesn't exist)
          if (!this.message.emailClassifications) {
            this.message.emailClassifications = [];
          }
          this.message.emailClassifications.push(newClassification);

          this.isSaving = false;
          this.onClose.next(true);
          this.activeModal.close(true);
        },
        error: (error) => {
          console.error('Failed to change classification:', error);
          this.isSaving = false;
          this.changeDetectorRef.detectChanges();

          Swal.fire({
            title: 'Грешка',
            text: 'Неуспешна промяна на класификацията. Моля, опитайте отново.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.onClose.next(false);
    this.activeModal.close(false);
  }

  /**
   * Get display label for classification category
   */
  getClassificationLabel(category: EmailClassificationCategory): string {
    const option = this.classificationOptions.find(opt => opt.value === category);
    return option ? option.label : 'Неизвестно';
  }
}

